import React, { FC, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { SearchResultList } from './search-results-list';
import { Flex } from '$components/layouts';
import { useTranslation } from '$hooks';
import { formatString } from '$lib/helpers';
import { SearchContentTypes } from '~/modules/search';
import { key } from '~/utils/key';
import { IFullSearchResult } from '$models';
import { useSearchData } from '../hooks/single-search';
import { FacetList } from '../helpers';
import { ProductList } from './product-list';
import { Text } from '$components/elements/text';
import { Loading } from '$components/elements/loading';
import { Headline } from '$components/elements/headline';
import { Link } from '$components/elements/link';

export type SearchModuleProps = {
    searchQuery?: string;
    searchInfo?: boolean;
    hideOnNoResults?: boolean;
    filterId?: string;
    pid?: string;
    mode?: SearchContentTypes;
    initialData?: IFullSearchResult[];
    pageId?: string;
    sId?: string;
};

export const SearchModule: FC<SearchModuleProps> = ({
    searchInfo,
    searchQuery = '',
    hideOnNoResults,
    filterId,
    mode,
    initialData,
    pid,
    pageId,
    sId,
}) => {
    const { translate } = useTranslation();
    const [oldDataKey, setOldDataKey] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [userInteracted, setUserInteracted] = useState(false);

    const {
        articles,
        authors,
        categories,
        series,
        productResults,
        searchTerm,
        totalSearchResults,
        loadingPage,
        loadNextPage,
        setFacets,
        dataKey,
    } = useSearchData(searchQuery, mode, filterId, initialData, mode !== 'all', pid, pageId, sId, userInteracted);

    // Set loading to false on every result update.
    useEffect(() => {
        if (oldDataKey !== dataKey) {
            setOldDataKey(dataKey);
            setIsLoading(false);
        }
    }, [dataKey]);

    const onSetFacets = (newFacets: FacetList) => {
        setIsLoading(true);
        setFacets(newFacets);
        setUserInteracted(true);
    };

    if (hideOnNoResults && !totalSearchResults) {
        return <></>;
    }

    return (
        <SearchModuleWrapper column>
            {isLoading && <Loading showBackdrop />}
            {searchInfo && (
                <SearchInformation>
                    <Headline>{translate('search.searchResultPage.searchPageTitle')}</Headline>
                    {searchTerm && (
                        <p>
                            {formatString(
                                translate('search.searchResultPage.searchResults'),
                                searchTerm,
                                totalSearchResults
                            )}
                        </p>
                    )}
                    {!!categories?.content?.length && (
                        <CategoryListing>
                            {formatString(translate('search.searchResultPage.seeCategories'))}:{' '}
                            {categories?.content
                                ?.filter((category) => !!category.heading && !!category.url)
                                .map((category) => (
                                    <Link themedLink href={category.url} key={key(category)}>
                                        {category.heading}
                                    </Link>
                                ))}
                        </CategoryListing>
                    )}
                </SearchInformation>
            )}

            <SearchResults column gap="medium">
                {productResults?.lastOrDefault()?.totalDocumentsFound ? (
                    <ProductList
                        facetInitData={initialData?.[0]}
                        productResult={productResults}
                        onLoadNextPage={() => loadNextPage(SearchContentTypes.Products)}
                        onSelectedFacets={onSetFacets}
                        loadingPage={loadingPage}
                        filterId={filterId}
                        pId={pid}
                        pageId={pageId}
                        sId={sId}
                    />
                ) : (
                    <NoProductMessage>
                        <Text dimmed>{translate('search.searchResultPage.noProductsToShow')}</Text>
                    </NoProductMessage>
                )}
                {!!authors && (
                    <SearchResultList
                        authors={authors}
                        title={formatString(
                            translate('search.searchResultPage.persons'),
                            authors.totalDocumentsFound || 0
                        )}
                        onLoadMore={() => loadNextPage(SearchContentTypes.Authors)}
                        isLoadingMore={loadingPage}
                    />
                )}
                {!!articles && (
                    <SearchResultList
                        articles={articles}
                        title={formatString(
                            translate('search.searchResultPage.articles'),
                            articles.totalDocumentsFound || 0
                        )}
                        onLoadMore={() => loadNextPage(SearchContentTypes.Articles)}
                        isLoadingMore={loadingPage}
                    />
                )}
                {!!series && (
                    <SearchResultList
                        series={series}
                        title={formatString(
                            translate('search.searchResultPage.series'),
                            series.totalDocumentsFound || 0
                        )}
                        onLoadMore={() => loadNextPage(SearchContentTypes.Series)}
                        isLoadingMore={loadingPage}
                    />
                )}
            </SearchResults>
        </SearchModuleWrapper>
    );
};

const SearchModuleWrapper = styled(Flex)({});

const SearchResults = styled(Flex)({});

const SearchInformation = styled.div({});

const NoProductMessage = styled.div({});

const CategoryListing = styled.p(({ theme: { space } }) => ({
    [`& > *:not(:last-child):after`]: {
        paddingRight: space[2],
        content: '", "',
    },
}));
